import { FC, useMemo } from "react";

import { MapLibreGeoJsonLine } from "@ryde/layout";
import { TLatLngTuple } from "@ryde/shared";

import {
  PLANNED_LINE_OPACITY,
  PLANNED_LINE_WEIGHT,
  TRAIL_OFFSET_PX,
  TRAIL_ROUTE_COLOR,
} from "../investigation.constants";
import { toLineFeature } from "../investigation.utils";

/**
 * Planned route line, shifted a few screen pixels to the side so it runs parallel to (not underneath) the
 * GPS trail — the trail keeps its true path (the vehicle rides it), and the planned line reads as a clearly
 * separate reference.
 *
 * The shift is MapLibre's own `line-offset` paint property, so the geometry published to the source is the
 * TRUE route and the separation is applied in screen space on the GPU every frame. Displacing the
 * coordinates instead (as the Leaflet original had to) bakes a pixel distance into lat/lng at whatever zoom
 * happened to be current: at the map's initial zoom of 7 those 12 px are ~6.2 km per point, which is what
 * turned this line into a fan of kilometre-long spikes.
 */
export const InvestigationPlannedRoute: FC<{
  positions: TLatLngTuple[];
}> = ({ positions }) => {
  const feature = useMemo(() => toLineFeature(positions), [positions]);

  if (positions.length < 2) {
    return null;
  }

  return (
    <MapLibreGeoJsonLine
      id="investigation-planned"
      feature={feature}
      color={TRAIL_ROUTE_COLOR}
      lineWidth={PLANNED_LINE_WEIGHT}
      opacity={PLANNED_LINE_OPACITY}
      lineOffsetPx={TRAIL_OFFSET_PX}
      withArrows
    />
  );
};
